import type { LuxPlusLandingNavigationAction } from 'luxPlus/contexts/LuxPlusLandingNavigationContext'

export interface BenefitCtaConfig {
  path?: string
  /**
   * DOM element ID to scroll to when the benefit tile is clicked.
   * Must match the `id` attribute on the target carousel/section.
   */
  scrollTarget?: string
  /**
   * Filter category to auto-select on the target carousel after scrolling.
   * Dispatches a 'luxplus-filter-select' custom event on the scroll target element.
   */
  filterCategory?: string
  /**
   * Key of a navigation action on LuxPlusLandingNavigationContextValue to invoke
   * when the benefit tile is clicked (e.g. switch to a specific tab).
   */
  navigationContextAction?: LuxPlusLandingNavigationAction
}

export const SUPPORTED_BENEFIT_TYPES: Array<App.LuxPlusBenefitType> = [
  'discounted_flash',
  'discounted_flights',
  'discounted_insurance',
  'discounted_tours',
  'early_access',
  'exclusive_offers',
  'hotel_inclusions',
  'priority_support',
  'cruise_inclusions',
  'discounted_cruise',
  'discounted_experiences',
  'partner_promo_code',
  'promo',
]

export const BENEFITS_CTA_CONFIG: Record<App.LuxPlusBenefitViewType, BenefitCtaConfig> = {
  discounted_flash: {
    scrollTarget: 'discounted_flash',
    filterCategory: 'member-discounts',
  },
  discounted_flights: {
    scrollTarget: 'lux-plus-landing-flights-banner',
  },
  hotel_inclusions: {
    scrollTarget: 'discounted_flash',
    filterCategory: 'bonus-inclusions',
  },
  exclusive_offers: {
    scrollTarget: 'discounted_flash',
    filterCategory: 'hidden-offers',
  },
  early_access: {
    scrollTarget: 'discounted_flash',
    filterCategory: 'early-access',
  },
  discounted_tours: {
    scrollTarget: 'discounted_tours',
  },
  discounted_cruise: {
    scrollTarget: 'cruise_inclusions',
  },
  cruise_inclusions: {
    scrollTarget: 'cruise_inclusions',
  },
  cruise_benefits: {
    scrollTarget: 'cruise_inclusions',
  },
  discounted_experiences: {
    scrollTarget: 'discounted_experiences',
  },
  discounted_insurance: {
    path: '/travel-protection',
  },
  priority_support: {},
  partner_promo_code: {},
  promo: {},
  voucher: {},
  discounted_offers: {},
  partner_promos: {
    navigationContextAction: 'navigateToPartnerOffers',
  },
}

/**
 * Sentinel description returned by svc-membership for benefits that have no
 * real copy yet. Matched in `useLuxPlusBenefitsView` to strip placeholders.
 * If the backend changes this value, benefits will render the raw placeholder
 * text — update this constant to match the new value.
 */
export const DEFAULT_BENEFIT_DESCRIPTION = 'Short description would go here and explain what the benefit is'

export const SAVE_THOUSANDS_REGIONS = ['ZA', 'TH', 'KR', 'ID', 'JP', 'TW', 'PH', 'VN']

/**
 * LuxPlus+ flight prices are currently only available in AU. The Optimizely
 * flag controls rollout, but is not region-scoped enough to be the source of
 * truth for product availability on the landing page.
 */
export const LUX_PLUS_FLIGHTS_PRICING_SUPPORTED_REGIONS = new Set(['AU'])

export interface BenefitGroupConfig {
  readonly types: ReadonlyArray<App.LuxPlusBenefitType>
}

export const BENEFIT_GROUP_CONFIGS: Record<App.LuxPlusBenefitGroupType, BenefitGroupConfig> = {
  discounted_offers: {
    types: ['discounted_flash', 'discounted_experiences', 'discounted_tours'],
  },
  partner_promos: {
    types: ['partner_promo_code'],
  },
  cruise_benefits: {
    types: ['cruise_inclusions', 'discounted_cruise'],
  },
}
